import $ from 'jquery';
import { DeterministicWrapperView } from '@bingads-webui/deterministic-wrapper-view';
import { updateGridQuery } from '@bingads-webui-campaign/summary-grid-helper';
import { TargetingSettingsContext } from '@bingads-webui-campaign/targeting-settings-scaffolds';
import { InlineLocationRecommendationPanel } from '@bingads-webui-campaign/inline-location-recommendation';
import { pgridFactory } from '@bingads-webui-campaign/pgrid-factory';
import { BudgetSplitView } from '@bingads-webui-campaign/campaignui-common-columns';
import { GridCustomizedPopupEditor, getEditorPositionAsideAnchor } from '@bingads-webui-campaign/grid-customized-popup-editor';

const EMPTY_TEMPLATE = () => '';

export class SettingsGridView extends DeterministicWrapperView {
  viewFactory({
    router,
    preferenceConfig,
    getGridConfig,
    getGridPlugins,
    filterService,
    setupDataSourceErrorHandling,
    prefetchChunks,
    instrumentation,
  }) {
    setupDataSourceErrorHandling(this);

    const gridConfig = getGridConfig(this);

    this.filterService = filterService;
    this.router = router;
    this.gridRemoved = false;

    let initialGrid = true;
    this.grid = pgridFactory({
      gridConfig,
      plugins: getGridPlugins(this),
      preferenceConfig,
    });

    this.instrumentation = instrumentation;

    TargetingSettingsContext.logger.logMessage(
      'ClientTrace',
      { name: 'Info', value: 0 },
      'SettingsGridView.buildGridFactory',
      'Settings grid created.'
    );

    this.filterService.on('filter-state-changed', () => {
      if (!this.gridRemoved) {
        updateGridQuery(
          this.grid.gridView,
          this.filterService.getCombinedFilter(), true
        );
      }
    });

    this.grid.gridView.on('willUpdate', (changes) => {
      // skip the preference write for sortable header when initial the grid
      // as this value is already read from preference.
      if (initialGrid) {
        initialGrid = false;
        return;
      }
      TargetingSettingsContext.handleSortChange(changes, this.grid.gridView, gridConfig.name);
    });

    this.grid.gridView.once('didReload', () => {
      this.grid.gridView.once('didRedraw', () => {
        if (typeof prefetchChunks === 'function') {
          prefetchChunks();
        }
      });
    });

    return this.grid.gridView;
  }

  renderTrunk() {
    if (this.gridRemoved) {
      return this;
    }

    this.filterService.refreshSource();
    this.filterService.initializeCombinedFilterPromise.then(() => {
      if (!this.gridRemoved) {
        updateGridQuery(
          this.grid.gridView,
          this.filterService.getCombinedFilter(), true
        );
      }
    });
    super.renderTrunk();
    return this;
  }

  get events() {
    return {
      'click .inline-location-target-recommendation': 'showInlineLocationRecommendation',
      'click .experiment-budget-ellipsis': 'showExperimentBudgetPopup',
    };
  }

  showExperimentBudgetPopup(event) {
    const item = this.grid.gridView.itemOfElement(event.target);
    const budgetSplitView = new BudgetSplitView({
      item,
      i18n: TargetingSettingsContext.i18n,
    }).render();

    return new GridCustomizedPopupEditor({
      editorView: budgetSplitView,
      editorPositionFactory: getEditorPositionAsideAnchor($(event.target)),
      renderBelow: false,
      className: 'grid-popupeditor experiment-budget-popup',
    }).render();
  }

  showInlineLocationRecommendation(event) {
    const campaign = this.grid.gridView.itemOfElement(event.target);
    const InlineLocationRecommendationModal = InlineLocationRecommendationPanel;
    this.inlineLocationRecommendationView = new InlineLocationRecommendationModal({
      scope: {
        customerId: TargetingSettingsContext.state.get('customer_id'),
        accountId: TargetingSettingsContext.state.get('account_id'),
        campaignId: campaign.Id,
      },
      deps: TargetingSettingsContext,
    }).render();
    this.inlineLocationRecommendationView.on('refresh:recommendations', () => this.forceRefreshGrid()); // when invalid recommendations, force refresh grid
  }

  forceRefreshGrid() {
    TargetingSettingsContext.CampaignCacheManager.invalidate('Campaign');
    this.grid.gridView.refresh(true);
  }

  get className() { return 'settings-grid-view'; }

  get template() {
    return EMPTY_TEMPLATE;
  }

  remove() {
    if (this.inlineLocationRecommendationView) {
      this.inlineLocationRecommendationView.remove();
    }
    if (this.grid) {
      if (this.grid.toolbar && this.grid.toolbar.gridToolbarGroup) {
        this.grid.toolbar.gridToolbarGroup.remove();
        this.grid.toolbar.gridToolbarGroup = null;
      }

      if (this.grid.gridView) {
        this.grid.gridView.trigger('willRemove');
        this.grid.gridView.remove();
        this.grid.gridView = null;
        this.view = null;
      }
    }
    this.gridRemoved = true;
  }
}
